import { apiGet, apiPost, apiPut } from '../apiService';
import { isDepartmentRequired } from '../../shared/constants/profileConstants';

const PipelineService = {
    getPipeline: async (orgId) => {
        if (!orgId) {
            throw new Error('Organization ID (orgId) is required to fetch pipeline data');
        }
        return await apiGet('api/pipeline', {
            params: { orgId }
        });
    },
    addTargetSegment: async (targetSegment) => {
        const requiredFields = ['propelAuthId', 'orgId', 'industry', 'companySize', 'geographicFocus', 'name'];
        const missingFields = requiredFields.filter(field => !targetSegment || !targetSegment[field]);
        if (missingFields.length > 0) {
            throw new Error(`Missing required fields for adding a target segment: ${missingFields.join(', ')}`);
        }
        return await apiPost('api/pipeline/target-segment', {
            ...targetSegment,
            geographicFocus: {
                geographicFocus: targetSegment.geographicFocus.geographicFocus,
                regions: targetSegment.geographicFocus.regions || [],
                states: targetSegment.geographicFocus.states || [],
                countries: targetSegment.geographicFocus.countries || [],
                internationalRegions: targetSegment.geographicFocus.internationalRegions || []
            }
        });
    },
    generateSegmentAccount: async (orgId, targetSegmentProfileId) => {
        const missingFields = [];
        if (!orgId) missingFields.push('orgId');
        if (!targetSegmentProfileId) missingFields.push('targetSegmentProfileId');
        if (missingFields.length > 0) {
            throw new Error(`Missing required fields to generate an account: ${missingFields.join(', ')}`);
        }
        return await apiPost('api/pipeline/generate-account', {
            orgId,
            targetSegmentProfileId,
        });
    },
    saveSegmentCustomizations: async (orgId, userId, segmentId, customizations) => {
        if (!orgId || !userId || !segmentId) {
            throw new Error('orgId, userId, and segmentId are required to save customizations');
        }
        return await apiPut(`api/pipeline/target-segment/${segmentId}/customizations`, {
            orgId,
            userId,
            customizations,
        });
    },
    addContactProfile: async (contactProfile) => {
        const requiredFields = ['orgId', 'userId', 'level'];
        if (isDepartmentRequired(contactProfile.level)) {
            requiredFields.push('department');
        }
        const missingFields = requiredFields.filter(field => !contactProfile || !contactProfile[field]);
        if (missingFields.length > 0) {
            throw new Error(`Missing required fields for adding a contact profile: ${missingFields.join(', ')}`);
        }
        return await apiPost('api/pipeline/contact-profile', contactProfile);
    },
    archiveAccount: async (accountId, userId) => {
        if (!accountId || !userId) {
            throw new Error('accountId and userId are required to archive an account');
        }
        return await apiPut(`api/pipeline/account/${accountId}/archive`, {
            userId,
        });
    },
    unarchiveAccount: async (accountId, userId) => {
        if (!accountId || !userId) {
            throw new Error('accountId and userId are required to unarchive an account');
        }
        return await apiPut(`api/pipeline/account/${accountId}/unarchive`, {
            userId,
        });
    },
    archiveContactProfile: async (contactProfileId, userId) => {
        if (!contactProfileId || !userId) {
            throw new Error('contactProfileId and userId are required to archive a contact profile');
        }
        return await apiPut(`api/pipeline/contact-profile/${contactProfileId}/archive`, {
            userId,
        });
    },
    deleteTargetSegment: async (segmentId, orgId, userId) => {
        if (!segmentId || !orgId || !userId) {
            throw new Error('segmentId, orgId, and userId are required to delete a target segment');
        }
        return await apiPut(`api/pipeline/target-segment/${segmentId}/archive`, {
            orgId,
            userId,
        });
    },
    updateTargetSegment: async (segmentId, updatedFields) => {
        if (!segmentId) throw new Error('segmentId is required to update a target segment');
        return await apiPut(`api/pipeline/target-segment/${segmentId}`, updatedFields);
    },
    getSuggestedNextCall: async (orgId, userId) => {
        if (!orgId || !userId) return null;
        try {
            const response = await apiGet('api/pipeline/get-next-call', {
                params: { orgId, userId }
            });
            if (response && response.persona) {
                return { ...response.persona, reason: response.reason };
            }
            return null;
        } catch (err) {
            // Optionally log error
            return null;
        }
    },
    archivePersona: async (personaId, userId) => {
        if (!personaId || !userId) {
            throw new Error('personaId and userId are required to archive a persona');
        }
        return await apiPut(`api/pipeline/persona/${personaId}/archive`, {
            userId,
        });
    },
};

export default PipelineService;
